/**
 * Confirm Delete Modal Component
 * Generic confirmation modal for delete actions
 */

import React, { useState, useCallback } from 'react';
import { Modal, Button, Stack, Flex, useTheme, spacing, fontSize } from '@fti/design-system';

export interface ConfirmDeleteModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => Promise<void>;
  title: string;
  message: string;
  itemName?: string;
  confirmButtonText?: string;
}

/**
 * Modal for confirming delete actions
 */
export function ConfirmDeleteModal({
  isOpen,
  onClose,
  onConfirm,
  title,
  message,
  itemName,
  confirmButtonText = 'Delete',
}: ConfirmDeleteModalProps) {
  const { theme } = useTheme();
  const [isDeleting, setIsDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const textColor = theme.colors.text.primary;
  const mutedColor = theme.colors.text.secondary;
  const errorColor = theme.colors.semantic.error.default;

  const handleConfirm = useCallback(async () => {
    setError(null);
    setIsDeleting(true);

    try {
      await onConfirm();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Operation failed');
    } finally {
      setIsDeleting(false);
    }
  }, [onConfirm, onClose]);

  const handleClose = useCallback(() => {
    if (!isDeleting) {
      setError(null);
      onClose();
    }
  }, [isDeleting, onClose]);

  return (
    <Modal isOpen={isOpen} onClose={handleClose}>
      <Modal.Header onClose={handleClose}>{title}</Modal.Header>
      <Modal.Body>
        <Stack gap="md">
          <p style={{ margin: 0, color: textColor }}>{message}</p>

        {itemName && (
          <div
            style={{
              padding: spacing.sm,
              background: theme.colors.background.paper,
              borderRadius: '6px',
              fontWeight: 500,
              color: textColor,
            }}
          >
            {itemName}
          </div>
        )}

        <p
          style={{
            margin: 0,
            color: errorColor,
            fontSize: fontSize.sm,
            fontWeight: 500,
          }}
        >
          This action cannot be undone.
        </p>

        {error && (
          <div
            style={{
              padding: spacing.sm,
              background: `${errorColor}15`,
              border: `1px solid ${errorColor}`,
              borderRadius: '6px',
              color: errorColor,
              fontSize: fontSize.sm,
            }}
          >
            {error}
          </div>
        )}

        </Stack>
      </Modal.Body>
      <Modal.Footer>
        <Flex justify="end" gap="sm">
          <Button
            variant="outline"
            color="primary"
            onClick={handleClose}
            disabled={isDeleting}
          >
            Cancel
          </Button>
          <Button
            variant="solid"
            color="error"
            onClick={handleConfirm}
            disabled={isDeleting}
          >
            {isDeleting ? 'Deleting...' : confirmButtonText}
          </Button>
        </Flex>
      </Modal.Footer>
    </Modal>
  );
}

export default ConfirmDeleteModal;
